/**
 * The curated visual system for photo-less listings.
 *
 * PURE and browser-safe: no database, no network, no `process.env`. The card,
 * the detail page, the admin thumbnail and the enrichment writer all resolve a
 * placeholder through the SAME functions here, so a listing cannot look one way
 * in a grid and another way on its own page.
 *
 * Nothing in here is random at render time. Every choice is either a value
 * SAVED on the listing at enrichment time, or derived deterministically from a
 * stable seed (the listing id), so the same listing always draws the same art.
 */

/** The semantic themes. One per commercial intent, not one per category row. */
export const PLACEHOLDER_THEMES = [
    'vehicles',
    'crypto',
    'electronics',
    'software',
    'web',
    'social',
    'services',
    'real-estate',
    'jobs',
    'fashion',
    'sports',
    'tickets',
    'home',
    'media',
    'generic'
] as const

export type PlaceholderTheme = (typeof PLACEHOLDER_THEMES)[number]

export type ThemeDefinition = {
    /** Short human label drawn on the art. Never a marketing claim. */
    label: string
    /**
     * Lucide icon names, most representative first. The extra names are what
     * lets two listings in the same theme differ without either being random:
     * the seed picks one and the choice is then saved.
     */
    icons: Array<string>
    /** The palette this theme leans on when the seed does not say otherwise. */
    palette: PalettePreference
}

type PalettePreference = Array<string>

/**
 * Theme definitions. `icons` are names, never components, so this module stays
 * importable from server code that must not pull React into its bundle.
 */
export const THEMES: Record<PlaceholderTheme, ThemeDefinition> = {
    'vehicles': { label: 'Vehicles', icons: ['Car', 'Gauge', 'Wrench'], palette: ['steel', 'slate', 'charcoal'] },
    'crypto': { label: 'Crypto', icons: ['Bitcoin', 'Coins', 'ArrowLeftRight'], palette: ['gold', 'ink', 'sand'] },
    'electronics': { label: 'Electronics', icons: ['Smartphone', 'Laptop', 'Headphones'], palette: ['slate', 'steel', 'paper'] },
    'software': { label: 'Software', icons: ['Code', 'Monitor', 'KeyRound'], palette: ['ink', 'charcoal', 'steel'] },
    'web': { label: 'Web', icons: ['Globe', 'Monitor', 'Code'], palette: ['mint', 'steel', 'olive'] },
    'social': { label: 'Accounts', icons: ['AtSign', 'Users', 'MessageCircle'], palette: ['ember', 'clay', 'paper'] },
    'services': { label: 'Services', icons: ['Briefcase', 'Wrench', 'Sparkles'], palette: ['olive', 'sand', 'paper'] },
    'real-estate': { label: 'Property', icons: ['Building2', 'House', 'KeyRound'], palette: ['sand', 'clay', 'gold'] },
    'jobs': { label: 'Jobs', icons: ['Briefcase', 'Users'], palette: ['charcoal', 'olive', 'slate'] },
    'fashion': { label: 'Fashion', icons: ['Shirt', 'ShoppingBag'], palette: ['clay', 'ember', 'sand'] },
    'sports': { label: 'Sports', icons: ['Trophy', 'Activity', 'Dumbbell'], palette: ['mint', 'olive', 'steel'] },
    'tickets': { label: 'Tickets', icons: ['Ticket', 'Calendar'], palette: ['ember', 'gold', 'paper'] },
    'home': { label: 'Home', icons: ['Sofa', 'Lamp'], palette: ['sand', 'paper', 'clay'] },
    'media': { label: 'Media', icons: ['Video', 'Music'], palette: ['ink', 'ember', 'charcoal'] },
    'generic': { label: 'Listing', icons: ['Package', 'Tag'], palette: ['paper', 'slate', 'sand'] }
}

/**
 * Every icon name the system may use, derived from the themes above so the
 * model's allowed list and the component's icon map can never drift apart.
 */
export const PLACEHOLDER_ICONS: Array<string> = Array.from(
    new Set(Object.values(THEMES).flatMap((theme) => theme.icons))
).sort()

export type PaletteDefinition = {
    /** The plate colour behind everything. */
    base: string
    /** The pattern and the inner plate. Always close to `base`. */
    wash: string
    /** Icon, label and rule. Contrast-checked against `base` at 4.5:1 or better. */
    ink: string
}

/**
 * Eleven brand-compatible palettes. Warm paper, near-black ink and muted
 * naturals, per the MTA design system: no gradients as decoration, no purple
 * or blue SaaS tints, and never a colour that competes with the one brand red.
 *
 * Every `ink` value is a dark-on-light or light-on-dark pairing measured above
 * the 4.5:1 body-text floor, because the placeholder carries a readable label.
 */
export const PALETTES: Record<string, PaletteDefinition> = {
    paper: { base: '#f1eee5', wash: '#e2ddcf', ink: '#1b1a17' },
    sand: { base: '#ece2d0', wash: '#dbcdb2', ink: '#2a2318' },
    clay: { base: '#eddcd3', wash: '#dcc3b5', ink: '#33221a' },
    olive: { base: '#e3e6d4', wash: '#ccd3b5', ink: '#23281a' },
    steel: { base: '#dfe3e6', wash: '#c5ced3', ink: '#1a2126' },
    mint: { base: '#d9e8dd', wash: '#bad5c3', ink: '#16261d' },
    ember: { base: '#f6dcd8', wash: '#eec1b8', ink: '#3a1512' },
    gold: { base: '#f0e4c6', wash: '#dfcd9c', ink: '#302713' },
    slate: { base: '#e2e0da', wash: '#cbc8bf', ink: '#201f1b' },
    ink: { base: '#1a1a18', wash: '#2c2c28', ink: '#f2efe6' },
    charcoal: { base: '#23211d', wash: '#35322b', ink: '#ece6d7' }
}

export const PALETTE_IDS = Object.keys(PALETTES)

/** Flat, printed textures. No photographs, no gradients used as decoration. */
export const PATTERNS = ['grid', 'dots', 'diagonal', 'rings', 'rule'] as const

export type PlaceholderPattern = (typeof PATTERNS)[number]

export type ResolvedPlaceholder = {
    theme: PlaceholderTheme
    icon: string
    palette: string
    pattern: PlaceholderPattern
    label: string
    colors: PaletteDefinition
}

/**
 * A small, stable string hash. Not cryptographic and not meant to be: its only
 * job is to turn a listing id into the same number on the server and in the
 * browser, so a placeholder never changes between the two renders.
 */
export const seedHash = (seed: string): number => {
    let hash = 2166136261

    for (let index = 0; index < seed.length; index += 1) {
        hash ^= seed.charCodeAt(index)
        hash = Math.imul(hash, 16777619)
    }

    return Math.abs(hash)
}

const pick = <T,>(items: Array<T>, seed: number, offset: number): T =>
    items[(seed + offset) % items.length]

export const isPlaceholderTheme = (value: unknown): value is PlaceholderTheme =>
    typeof value === 'string' && (PLACEHOLDER_THEMES as ReadonlyArray<string>).includes(value)

export const isPlaceholderPattern = (value: unknown): value is PlaceholderPattern =>
    typeof value === 'string' && (PATTERNS as ReadonlyArray<string>).includes(value)

/**
 * The single resolution rule, used by the writer and by every render site.
 *
 * A SAVED value always wins, which is what guarantees a listing keeps the
 * appearance it was given at enrichment time. Anything missing (an older row, a
 * surface whose query does not select the columns) falls back to a value
 * derived from the seed, so the art is still stable and still designed — never
 * an empty grey box, and never a fresh random choice on each render.
 */
export const resolvePlaceholder = (input: {
    theme?: string | null
    icon?: string | null
    palette?: string | null
    pattern?: string | null
    /** Stable per listing. The listing id, in practice. */
    seed: string
}): ResolvedPlaceholder => {
    const seed = seedHash(input.seed || 'listing')
    const theme: PlaceholderTheme = isPlaceholderTheme(input.theme) ? input.theme : 'generic'
    const definition = THEMES[theme]

    const icon = input.icon && input.icon.trim()
        ? input.icon.trim()
        : pick(definition.icons, seed, 0)

    const palette = input.palette && PALETTES[input.palette]
        ? input.palette
        : pick(definition.palette, seed, 1)

    const pattern = isPlaceholderPattern(input.pattern)
        ? input.pattern
        : pick([...PATTERNS], seed, 2)

    return {
        theme,
        icon,
        palette,
        pattern,
        label: definition.label,
        colors: PALETTES[palette] ?? PALETTES.paper
    }
}

/**
 * The pattern as a CSS background. Returned as plain declarations rather than
 * utility classes because the colours are runtime data, which is the one case
 * the design system allows an inline `style` for.
 */
export const patternStyle = (
    pattern: PlaceholderPattern,
    colors: PaletteDefinition
): { backgroundImage: string, backgroundSize: string } => {
    const line = colors.wash

    if (pattern === 'dots') {
        return {
            backgroundImage: `radial-gradient(${line} 1.5px, transparent 1.6px)`,
            backgroundSize: '14px 14px'
        }
    }

    if (pattern === 'diagonal') {
        return {
            backgroundImage: `repeating-linear-gradient(135deg, ${line} 0 2px, transparent 2px 11px)`,
            backgroundSize: 'auto'
        }
    }

    if (pattern === 'rings') {
        return {
            backgroundImage: `repeating-radial-gradient(circle at 50% 50%, ${line} 0 1.5px, transparent 1.5px 22px)`,
            backgroundSize: 'auto'
        }
    }

    if (pattern === 'rule') {
        return {
            backgroundImage: `repeating-linear-gradient(0deg, ${line} 0 2px, transparent 2px 16px)`,
            backgroundSize: 'auto'
        }
    }

    return {
        backgroundImage:
            `linear-gradient(0deg, ${line} 1px, transparent 1px), `
            + `linear-gradient(90deg, ${line} 1px, transparent 1px)`,
        backgroundSize: '18px 18px'
    }
}

/**
 * Maps a canonical category SLUG onto a theme.
 *
 * The slugs are the ones the server actually holds, never the ones a model or a
 * brief guessed at: an unknown slug degrades to `generic` rather than inventing
 * a theme. Keep this in step with the `categories` table.
 */
export const THEME_BY_CATEGORY_SLUG: Record<string, PlaceholderTheme> = {
    'electronics': 'electronics',
    'usernames': 'social',
    'youtube-channels': 'media',
    'influencers': 'social',
    'vehicles': 'vehicles',
    'home-furniture': 'home',
    'fashion': 'fashion',
    'services': 'services',
    'digital-creative': 'web',
    'jobs-gigs': 'jobs',
    'real-estate': 'real-estate',
    'business-equipment': 'services',
    'sports-outdoors': 'sports',
    'software-licences': 'software',
    'tickets-events': 'tickets',
    'crypto-p2p': 'crypto',
    'social-accounts': 'social',
    'other': 'generic'
}

export const themeForCategorySlug = (slug: string | null | undefined): PlaceholderTheme =>
    (slug && THEME_BY_CATEGORY_SLUG[slug]) || 'generic'
